import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	Paper,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
	EventRejectionReason,
	RejectionReasonTier,
} from "../../domain/rejectionReason.interface";
import {
	accountRejectionReasonService,
	ReasonReach,
} from "../../services/accountRejectionReason.service";

/**
 * The account's rejection-reason catalogue — SIMS-8498.
 *
 * 🔴 NO HI-FI EXISTS for this screen. The designer missed the page, confirmed by
 * the PO 2026-09-18, and the handoff lists the story under "Not designed". Built
 * from two shipped sources rather than invented:
 *   • `RejectionReasonsSection` (SIMS-8490, frame 3a) for the row vocabulary;
 *   • the account-config page family (/group-labels, /analytes, /cups, /roles)
 *     for the page shell.
 * Recorded in `aidlc-docs/SIMS-8190/SIMS-8498/inception/design/`.
 */

/**
 * 🔴 NOT the shipped `TIER_LABEL`. That renders EVENT as "This event", which is
 * deictic — true on the event form and meaningless on an account-wide screen
 * where no single event is in view. Deliberate divergence, not a copy that
 * drifted; the event form keeps its own wording.
 */
const TIER_LABEL: Record<RejectionReasonTier, string> = {
	[RejectionReasonTier.PLATFORM]: "Platform",
	[RejectionReasonTier.ACCOUNT]: "Account",
	[RejectionReasonTier.EVENT]: "Event-only",
};

const emptyDraft = (): {
	title: string;
	participantCopy: string;
	description: string;
	requiresNote: boolean;
} => ({ title: "", participantCopy: "", description: "", requiresNote: false });

export const RejectionReasons = (): React.ReactElement => {
	const [reasons, setReasons] = useState<EventRejectionReason[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");

	const [draft, setDraft] = useState<ReturnType<typeof emptyDraft> | null>(
		null,
	);
	const [editing, setEditing] = useState<EventRejectionReason | null>(null);
	const [retiring, setRetiring] = useState<EventRejectionReason | null>(null);
	const [reach, setReach] = useState<ReasonReach | null>(null);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setReasons(await accountRejectionReasonService.list());
			setError(null);
		} catch {
			setError("Could not load this account's rejection reasons.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const visible = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return reasons;
		return reasons.filter(
			(r) =>
				r.title.toLowerCase().includes(q) ||
				r.participantCopy.toLowerCase().includes(q) ||
				r.id.toLowerCase().includes(q),
		);
	}, [reasons, search]);

	/** AC3 — a platform reason is readable here and nothing else. */
	const isPlatform = (r: EventRejectionReason): boolean =>
		r.tier === RejectionReasonTier.PLATFORM;

	const openRetire = async (reason: EventRejectionReason): Promise<void> => {
		setRetiring(reason);
		setReach(null);
		try {
			setReach(await accountRejectionReasonService.reach(reason.id));
		} catch {
			setReach(null);
		}
	};

	const doRetire = async (withdraw: boolean): Promise<void> => {
		if (!retiring) return;
		setBusy(true);
		try {
			await accountRejectionReasonService.retire(retiring.id, withdraw);
			setRetiring(null);
			await load();
		} catch {
			setError("Could not retire that reason.");
		} finally {
			setBusy(false);
		}
	};

	return (
		<Box sx={{ p: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Rejection Reasons Management
				</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => setDraft(emptyDraft())}
				>
					Add Reason
				</Button>
			</Box>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Reasons your events can share. A reason written here can be added to any
				event in this account; the platform&apos;s own reasons are listed too
				and cannot be changed.
			</Typography>

			<Box mb={3}>
				<TextField
					fullWidth
					label="Search reasons"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ color: "action.active" }} />
							</InputAdornment>
						),
					}}
				/>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Stack alignItems="center" sx={{ py: 4 }}>
					<CircularProgress />
				</Stack>
			) : (
				<Stack spacing={1}>
					{visible.map((reason) => (
						<Paper key={reason.id} variant="outlined" sx={{ p: 1.5 }}>
							<Stack
								direction="row"
								spacing={1}
								alignItems="flex-start"
								justifyContent="space-between"
							>
								<Box sx={{ flexGrow: 1 }}>
									<Typography variant="subtitle2">{reason.title}</Typography>
									<Typography variant="body2" color="text.secondary">
										{reason.participantCopy}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{[
											reason.id,
											reason.requiresNote ? "requires an internal note" : "",
											/* AC3 — the admin is told WHY it is read-only. */
											isPlatform(reason)
												? "read-only · wording changes with a product release"
												: "",
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography>
								</Box>

								<Stack direction="row" spacing={1} alignItems="center">
									<Chip label={TIER_LABEL[reason.tier]} size="small" />

									{/* AC3 — no edit and no retire on a platform reason.
									    AC5 — and no action anywhere that demotes an account
									    reason back to a single event. */}
									{!isPlatform(reason) && (
										<>
											{/* 🔴 Explicit aria-labels naming the reason. A MUI
											    Tooltip sets the accessible name from its title, so a
											    wrapped button announces "Choose how far the
											    retirement reaches" instead of "Retire" — and with
											    several rows on screen, "Edit" alone never says which
											    reason it edits. Same idiom as the shipped
											    event-level section. */}
											<Button
												size="small"
												aria-label={`Edit ${reason.title}`}
												onClick={() => setEditing(reason)}
											>
												Edit
											</Button>
											<Button
												size="small"
												color="warning"
												aria-label={`Retire ${reason.title}`}
												onClick={() => void openRetire(reason)}
											>
												Retire
											</Button>
										</>
									)}
								</Stack>
							</Stack>
						</Paper>
					))}

					{visible.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							{search
								? "No reason matches that search."
								: "This account has not written any reasons yet. The platform's reasons are still available to every event."}
						</Typography>
					)}
				</Stack>
			)}

			<CreateDialog
				draft={draft}
				setDraft={setDraft}
				onSaved={load}
				setError={setError}
			/>
			<EditDialog
				reason={editing}
				onClose={() => setEditing(null)}
				onSaved={load}
				setError={setError}
			/>

			{/* AC6 — the two reaches, stated with what each one leaves behind. */}
			<Dialog
				open={Boolean(retiring)}
				onClose={() => setRetiring(null)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Retire “{retiring?.title}”</DialogTitle>
				<DialogContent>
					{reach === null ? (
						<CircularProgress size={20} />
					) : (
						<>
							<DialogContentText sx={{ mb: 2 }}>
								{reach.eventsHolding === 0
									? "No event currently offers this reason."
									: `${reach.eventsHolding} event${
											reach.eventsHolding === 1 ? "" : "s"
										} still offer this reason to coordinators.`}
							</DialogContentText>

							<Alert severity="info" sx={{ mb: 2 }}>
								Every rejection already recorded keeps the wording that
								participant received, whichever you choose.
							</Alert>

							{reach.eventsWithOwnWording > 0 && (
								<Alert severity="warning" sx={{ mb: 2 }}>
									{reach.eventsWithOwnWording} of them use their own wording for
									this reason. Withdrawing removes that wording too, and
									re-adding the reason later does not bring it back.
								</Alert>
							)}

							{reach.blocked.length > 0 && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{reach.blocked.map((b) => (
										<div key={b.eventId}>
											{b.eventName ?? b.eventId} — {b.why}
										</div>
									))}
								</Alert>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRetiring(null)}>Cancel</Button>
					<Button disabled={busy} onClick={() => void doRetire(false)}>
						Stop offering to new events
					</Button>
					{/* 🔴 The destructive one is never the default and never the
					    primary action. */}
					<Button
						disabled={busy}
						color="warning"
						onClick={() => void doRetire(true)}
					>
						Withdraw from all events
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

const CreateDialog = ({
	draft,
	setDraft,
	onSaved,
	setError,
}: {
	draft: ReturnType<typeof emptyDraft> | null;
	setDraft: (d: ReturnType<typeof emptyDraft> | null) => void;
	onSaved: () => Promise<void>;
	setError: (e: string | null) => void;
}): React.ReactElement => {
	const [busy, setBusy] = useState(false);
	if (!draft) return <></>;

	// AC2 — the same two required fields as SIMS-8490 AC6: a reason without a
	// label cannot be audited, and one without wording cannot be sent.
	const invalid = !draft.title.trim() || !draft.participantCopy.trim();

	return (
		<Dialog open onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
			<DialogTitle>Add a rejection reason</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Internal label"
						required
						value={draft.title}
						onChange={(e) => setDraft({ ...draft, title: e.target.value })}
						helperText="What the audit record shows. Not seen by participants."
					/>
					<TextField
						label="Participant wording"
						required
						multiline
						minRows={2}
						value={draft.participantCopy}
						onChange={(e) =>
							setDraft({ ...draft, participantCopy: e.target.value })
						}
						helperText="The exact sentence the participant receives."
					/>
					<TextField
						label="When to use it"
						multiline
						minRows={2}
						value={draft.description}
						onChange={(e) =>
							setDraft({ ...draft, description: e.target.value })
						}
						helperText="Guidance for coordinators, so two reviewers classify alike."
					/>
					<FormControlLabel
						control={
							<Switch
								checked={draft.requiresNote}
								onChange={(e) =>
									setDraft({ ...draft, requiresNote: e.target.checked })
								}
							/>
						}
						label="Requires a written internal note"
					/>
					{/* 🔴 The coupling neither AC states on its own: since SIMS-8215
					    AC2 a note-requiring reason is never offered to a validation
					    agent, because no agent can write one. */}
					{draft.requiresNote && (
						<Alert severity="info">
							A reason that requires a note is never offered to a validation
							agent, because an agent cannot write one. Only coordinators will
							be able to select it.
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setDraft(null)}>Cancel</Button>
				<Button
					variant="contained"
					disabled={invalid || busy}
					onClick={async () => {
						setBusy(true);
						try {
							await accountRejectionReasonService.create({
								title: draft.title.trim(),
								participantCopy: draft.participantCopy.trim(),
								description: draft.description.trim() || undefined,
								requiresNote: draft.requiresNote,
							});
							setDraft(null);
							await onSaved();
						} catch {
							setError("Could not create that reason.");
						} finally {
							setBusy(false);
						}
					}}
				>
					Add reason
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const EditDialog = ({
	reason,
	onClose,
	onSaved,
	setError,
}: {
	reason: EventRejectionReason | null;
	onClose: () => void;
	onSaved: () => Promise<void>;
	setError: (e: string | null) => void;
}): React.ReactElement => {
	const [title, setTitle] = useState("");
	const [participantCopy, setParticipantCopy] = useState("");
	const [requiresNote, setRequiresNote] = useState(false);
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<ReasonReach | null>(null);

	useEffect(() => {
		setTitle(reason?.title ?? "");
		setParticipantCopy(reason?.participantCopy ?? "");
		setRequiresNote(reason?.requiresNote ?? false);
		setResult(null);
	}, [reason]);

	if (!reason) return <></>;

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit “{reason.title}”</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					{/* 🔴 AC7's load-bearing clause, stated where it is relied on. */}
					<Alert severity="info">
						The code <strong>{reason.id}</strong> never changes, so rejection
						counts stay on one value.
					</Alert>

					<TextField
						label="Internal label"
						required
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<TextField
						label="Participant wording"
						required
						multiline
						minRows={2}
						value={participantCopy}
						onChange={(e) => setParticipantCopy(e.target.value)}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={requiresNote}
								onChange={(e) => setRequiresNote(e.target.checked)}
							/>
						}
						label="Requires a written internal note"
					/>

					<Alert severity="info">
						Rejections already recorded keep the wording their participant was
						sent. This changes what future rejections send.
					</Alert>

					{result && result.eventsWithOwnWording > 0 && (
						<Alert severity="warning">
							{result.eventsWithOwnWording} event
							{result.eventsWithOwnWording === 1 ? "" : "s"} use their own
							wording for this reason. They keep sending it — your change moves
							what <strong>Restore original</strong> returns them to.
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
				<Button
					variant="contained"
					disabled={busy || !title.trim() || !participantCopy.trim()}
					onClick={async () => {
						setBusy(true);
						try {
							const res = await accountRejectionReasonService.update(
								reason.id,
								{
									title: title.trim(),
									participantCopy: participantCopy.trim(),
									requiresNote,
								},
							);
							setResult(res.reach);
							await onSaved();
						} catch {
							setError("Could not save that change.");
						} finally {
							setBusy(false);
						}
					}}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RejectionReasons;
